import { z } from 'zod/v3'
import type {
    ShopAdminGroup,
    ShopAdminVariant,
    ShopGroupInput,
    ShopOCOP,
} from '@/lib/api/shop-catalog-api'

import { m } from '@/lib/paraglide/messages'

// Trạng thái form Sửa nhóm. Loại giữ thêm dữ liệu ERP chỉ đọc (SKU, tồn, giá)
// để hiện trên dòng; khi lưu chỉ gửi phần sửa được.

export interface VariantRow extends ShopAdminVariant {
    key: string
}

/** OCOP trong form: ô chữ là chuỗi (trống = null, năm/sao đổi sang số khi gửi). */
export interface OCOPForm {
    visible: boolean
    stars: string
    level: string
    year: string
    decision: string
    valid_until: string
    certificate_title: string
    certificate_image_url: string | null
}

export interface GroupForm {
    name: string
    slug: string
    category_ids: string[]
    tagline: string
    highlights: string[]
    variants: VariantRow[]
    brew: Record<keyof ShopGroupInput['brew'], string>
    clear_description_html: boolean
    /** HTML "Mô tả chi tiết" khi người dùng đã sửa; null = chưa đụng (giữ như cũ). */
    description_html: string | null
    /** HTML "Hướng dẫn pha" khi người dùng đã sửa; null = chưa đụng; '' = xoá. */
    brew_html: string | null
    related: { id: string; name: string }[]
    story_slug: string
    ocop: OCOPForm
    show_web: boolean
    show_miniapp: boolean
    best_seller_rank: string
    sort_order: string
    seo_title: string
    seo_description: string
    seo_image_url: string | null
}

const opt = (v: string) => (v.trim() === '' ? null : v)

// Luật khớp server (usecase/shop/catalog_text.go slugRe, groups.go TaglineMax).
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/
const SLUG_MAX = 80
const TAGLINE_MAX = 300
/** Cấp OCOP web / Mini App hiểu (server groups.go isOCOPLevel); '' = không ghi cấp (chỉ khi huy hiệu ẩn). */
export const OCOP_LEVELS = ['provincial', 'national'] as const

// Chữ cũ quen thuộc từ thời ô Cấp gõ tự do (không dấu, chữ thường) → mã web
// hiểu. Khớp legacyOCOPLevels ở server (usecase/shop/groups.go).
const LEGACY_OCOP_LEVELS: Record<string, (typeof OCOP_LEVELS)[number]> = {
    national: 'national',
    'quoc gia': 'national',
    'cap quoc gia': 'national',
    provincial: 'provincial',
    tinh: 'provincial',
    'cap tinh': 'provincial',
}

/** Mã chuẩn của cấp OCOP, hoặc giữ nguyên khi không nhận ra (bắt chọn lại). */
export function normalizeOCOPLevel(v: string): string {
    const key = v
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[đĐ]/g, 'd')
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .join(' ')
    return LEGACY_OCOP_LEVELS[key] ?? v
}

let seq = 0
export const newKey = () => `k${++seq}`

const EMPTY_OCOP: OCOPForm = {
    visible: false,
    stars: '',
    level: '',
    year: '',
    decision: '',
    valid_until: '',
    certificate_title: '',
    certificate_image_url: null,
}

function ocopForm(o: Partial<ShopOCOP>): OCOPForm {
    return {
        visible: o.visible ?? false,
        stars: o.stars ? String(o.stars) : '',
        level: normalizeOCOPLevel(o.level ?? ''),
        year: o.year ? String(o.year) : '',
        decision: o.decision ?? '',
        valid_until: o.valid_until ?? '',
        certificate_title: o.certificate_title ?? '',
        certificate_image_url: o.certificate_image_url ?? null,
    }
}

function ocopInput(o: OCOPForm): ShopOCOP {
    const num = (v: string) => (v.trim() === '' ? null : Number(v))
    return {
        visible: o.visible,
        stars: num(o.stars),
        level: opt(o.level),
        year: num(o.year),
        decision: opt(o.decision),
        valid_until: opt(o.valid_until),
        certificate_title: opt(o.certificate_title),
        certificate_image_url: o.certificate_image_url,
    }
}

export function emptyForm(): GroupForm {
    return {
        name: '',
        slug: '',
        category_ids: [],
        tagline: '',
        highlights: [],
        variants: [],
        brew: {
            tea_amount: '',
            water_amount: '',
            water_temp: '',
            steep_time: '',
            infusions: '',
            note: '',
        },
        clear_description_html: false,
        description_html: null,
        brew_html: null,
        related: [],
        story_slug: '',
        ocop: { ...EMPTY_OCOP },
        show_web: true,
        show_miniapp: true,
        best_seller_rank: '',
        sort_order: '0',
        seo_title: '',
        seo_description: '',
        seo_image_url: null,
    }
}

const s = (v: string | null | undefined) => v ?? ''

export function formFromGroup(g: ShopAdminGroup): GroupForm {
    return {
        name: g.name,
        slug: g.slug,
        category_ids: g.category_ids,
        tagline: s(g.tagline),
        highlights: g.highlights,
        variants: g.variants.map((v) => ({ ...v, key: newKey() })),
        brew: {
            tea_amount: s(g.brew.tea_amount),
            water_amount: s(g.brew.water_amount),
            water_temp: s(g.brew.water_temp),
            steep_time: s(g.brew.steep_time),
            infusions: s(g.brew.infusions),
            note: s(g.brew.note),
        },
        clear_description_html: false,
        description_html: null,
        brew_html: null,
        related: g.related.map((r) => ({ id: r.id, name: r.name })),
        story_slug: s(g.story_slug),
        ocop: ocopForm(g.ocop),
        show_web: g.show_web,
        show_miniapp: g.show_miniapp,
        best_seller_rank: g.best_seller_rank ? String(g.best_seller_rank) : '',
        sort_order: String(g.sort_order),
        seo_title: s(g.seo_title),
        seo_description: s(g.seo_description),
        seo_image_url: g.seo_image_url,
    }
}

/**
 * Kiểm nhanh ở FE (server vẫn kiểm đủ). Lỗi gắn đúng đường dẫn ô của form —
 * trùng tên ô server trả trong params.field — để hiện dưới ô qua ui/field.
 */
export const groupFormSchema = () =>
    z.custom<GroupForm>().superRefine((f, ctx) => {
        const fail = (path: (string | number)[], message: string) =>
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message })
        const badSlug = (v: string) =>
            v.trim() !== '' &&
            (v.trim().length > SLUG_MAX || !SLUG_RE.test(v.trim()))
        if (!f.name.trim()) fail(['name'], m.error_shop_field_required())
        if (badSlug(f.slug))
            fail(['slug'], m.error_shop_slug_invalid({ max: String(SLUG_MAX) }))
        if (f.tagline.trim().length > TAGLINE_MAX)
            fail(
                ['tagline'],
                m.error_shop_field_too_long({ max: String(TAGLINE_MAX) }),
            )
        // ERP-05: nhóm 0 loại không hiện trên web / Mini App.
        if (f.variants.length === 0)
            fail(['variants'], m.error_shop_group_variants_required())
        f.variants.forEach((v, i) => {
            if (!v.label.trim())
                fail(['variants', i, 'label'], m.error_shop_field_required())
        })
        if (badSlug(f.story_slug))
            fail(
                ['story_slug'],
                m.error_shop_slug_invalid({ max: String(SLUG_MAX) }),
            )
        // Huy hiệu đang ẩn: cấp trống / lạ còn sót không chặn lưu nhóm (server
        // cũng vậy). Bật hiện thì bắt chọn: web coi cấp trống là cấp tỉnh.
        const level = f.ocop.level.trim()
        if (f.ocop.visible && level === '')
            fail(['ocop', 'level'], m.error_shop_ocop_level_required())
        else if (
            f.ocop.visible &&
            !(OCOP_LEVELS as readonly string[]).includes(level)
        )
            fail(['ocop', 'level'], m.error_shop_ocop_level_invalid())
        if (f.best_seller_rank.trim() !== '') {
            const n = Number(f.best_seller_rank)
            if (!Number.isInteger(n) || n < 1)
                fail(['best_seller_rank'], m.error_shop_rank_invalid())
        }
        const order = Number(f.sort_order)
        if (f.sort_order.trim() === '' || !Number.isInteger(order) || order < 0)
            fail(['sort_order'], m.error_shop_sort_order_invalid())
    })

export function toInput(f: GroupForm): ShopGroupInput {
    return {
        name: f.name,
        slug: f.slug.trim(),
        category_ids: f.category_ids,
        tagline: opt(f.tagline),
        highlights: f.highlights.filter((h) => h.trim() !== ''),
        variants: f.variants.map((v) => ({
            product_id: v.product_id,
            label: v.label,
            show_web: v.show_web,
            show_miniapp: v.show_miniapp,
            in_stock: v.in_stock,
        })),
        brew: {
            tea_amount: opt(f.brew.tea_amount),
            water_amount: opt(f.brew.water_amount),
            water_temp: opt(f.brew.water_temp),
            steep_time: opt(f.brew.steep_time),
            infusions: opt(f.brew.infusions),
            note: opt(f.brew.note),
        },
        clear_brew_html: false,
        clear_description_html: f.clear_description_html,
        ...(f.description_html === null
            ? {}
            : { description_html: f.description_html }),
        ...(f.brew_html === null ? {} : { brew_html: f.brew_html }),
        related_ids: f.related.map((r) => r.id),
        story_slug: opt(f.story_slug),
        ocop: ocopInput(f.ocop),
        show_web: f.show_web,
        show_miniapp: f.show_miniapp,
        best_seller_rank:
            f.best_seller_rank.trim() === ''
                ? null
                : Number(f.best_seller_rank),
        sort_order: Number(f.sort_order),
        seo_title: opt(f.seo_title),
        seo_description: opt(f.seo_description),
        seo_image_url: f.seo_image_url,
    }
}
